import { buildWpwlOptions, LTR_INPUT_SELECTOR } from './options.js';
import widgetStyles from './widget.css?inline';

const SCRIPT_ID = 'hyperpay-script';
const STYLE_ID = 'hyperpay-widget-style';

/**
 * Manages the HyperPay payment widget lifecycle for the checkout page:
 *  - injects / removes the external paymentWidgets.js script,
 *  - configures `wpwlOptions` before the script loads,
 *  - keeps card inputs left-to-right inside the RTL layout,
 *  - tells Livewire when the widget is ready or failed (`onWidgetReady` / `onWidgetError`).
 *
 * The Livewire component starts it by dispatching `load-hyperpay-script` with `{ checkoutId, locale }`.
 */
export default function hyperPayWidget() {
    return {
        init() {
            // Clean up when Livewire navigates away (SPA mode).
            this.$el.addEventListener('livewire:navigating', () => this.cleanup());

            // Open the first payment form as soon as the page is ready. The server picks Apple Pay,
            // or Mada on a device that cannot pay with Apple Pay (its form would be empty there).
            this.$nextTick(() => this.$wire.selectDefaultMethod(this.applePayAvailable()));
        },

        applePayAvailable() {
            try {
                return typeof window.ApplePaySession !== 'undefined' && window.ApplePaySession.canMakePayments();
            } catch {
                return false;
            }
        },

        /** Handler for the `load-hyperpay-script` event. */
        loadScript(detail) {
            try {
                this.cleanup();
                this.resetWpwl();

                window.wpwlOptions = buildWpwlOptions(detail, {
                    onReady: () => this.fixInputDirections(),
                    onChangeBrand: () => setTimeout(() => this.fixInputDirections(), 100),
                });

                this.injectScript(detail);
            } catch (error) {
                this.fail(error, 'Unable to initialize the HyperPay widget.');
            }
        },

        injectScript(detail) {
            const baseUrl = import.meta.env.VITE_HYPERPAY_BASE_URL;

            if (!baseUrl) {
                throw new Error('VITE_HYPERPAY_BASE_URL is not configured.');
            }

            if (!detail?.checkoutId) {
                throw new Error('Missing HyperPay checkout ID.');
            }

            // Never inject twice (e.g. rapid method switches).
            if (document.getElementById(SCRIPT_ID)) {
                return;
            }

            const script = document.createElement('script');
            script.id = SCRIPT_ID;
            script.async = true;
            script.src = `${baseUrl}/v1/paymentWidgets.js?checkoutId=${encodeURIComponent(detail.checkoutId)}`;
            script.onerror = () => this.fail(new Error('Script load failed.'), 'Unable to load the HyperPay widget.');
            script.onload = () => {
                this.injectStyles();
                this.$wire.onWidgetReady();
            };

            document.body.appendChild(script);
        },

        resetWpwl() {
            try {
                window.wpwl?.unload?.();
            } catch (error) {
                console.warn('[HyperPay] wpwl.unload() threw:', error);
            }

            delete window.wpwl;
            delete window.wpwlOptions;
        },

        fixInputDirections() {
            document.querySelectorAll(LTR_INPUT_SELECTOR).forEach((element) => {
                element.style.setProperty('direction', 'ltr', 'important');
                element.style.setProperty('text-align', 'left', 'important');
                element.setAttribute('dir', 'ltr');
            });
        },

        injectStyles() {
            document.getElementById(STYLE_ID)?.remove();

            const style = document.createElement('style');
            style.id = STYLE_ID;
            style.textContent = widgetStyles;

            document.body.appendChild(style);
        },

        cleanup() {
            document.getElementById(SCRIPT_ID)?.remove();
            document.getElementById(STYLE_ID)?.remove();
        },

        fail(error, message) {
            this.cleanup();
            this.resetWpwl();
            console.error('[HyperPay]', message, error);
            this.$wire.onWidgetError();
        },
    };
}
